'use strict';

import React, {useState, useCallback, useMemo} from 'react';
import {Set as ImmutableSet} from 'immutable';

import {cssClasses} from '../../shared/utils';
import {useNodes, ToggleNode} from "./state";

function EventsTreeNode({node, active, expanded, onSelectToggle, onExpandToggle}) {
	const onNodeClick = useCallback((e) => {
		e.preventDefault();
		onSelectToggle(node);
	}, [node, onSelectToggle]);

	const onIconClick = useCallback((e) => {
		e.preventDefault();
		onExpandToggle(node);
	}, [node, onExpandToggle]);

	return useMemo(
		() => {
			const type = node.type;
			const level = type.length;
			const label = type[level - 1];
			const title = type.join('.');
			let hasChildren = Boolean(node.children.size > 0);
			return <div
				className={
					cssClasses(
						{
							active,
							'has-children': hasChildren,
							'expanded': active || expanded
						},
						`level-${level}`
					)
				}
			>
				{hasChildren && <a href="#" onClick={onIconClick} className="cm-icon"/>}
				<a href="#" title={title} onClick={onNodeClick} className="cm-label">{label}</a>
			</div>
		},
		[node, active, expanded, onNodeClick, onIconClick]
	);
}

function sortNodes(n1, n2) {
	let t1 = n1.type;
	let t2 = n2.type;
	let l1 = t1.length - 1;
	let l2 = t2.length - 1;
	let ml = Math.max(l1, l2);
	let i = 0;
	while (i <= ml) {
		if (i > l1) {
			return -1;
		}
		if (i > l2) {
			return 1;
		}
		let tkn1 = t1[i];
		let tkn2 = t2[i];
		let cmp = tkn1.localeCompare(tkn2);
		if (cmp) {
			return cmp;
		}
		i += 1;
	}
	return 0;
}

function shouldShow(node, subtree) {
	let nt = node.type;
	let st = subtree.type;
	let nl = nt.length - 1;
	let sl = st.length - 1;
	let ml = Math.max(nl, sl);
	let i = 0;
	while (i <= ml) {
		if (i > nl) {
			// Node is an ancestor of subtree
			return true;
		}
		if (i > sl) {
			// Node is a child of subtree
			return i === nl;
		}
		if (nt[i] !== st[i]) {
			// True if node is a sibling of subtree
			return i === nl && nl === sl;
		}
		i += 1;
	}
	// True if node is subtree
	return nt[i] === st[i];
}

/**
 * Returns true if node is a child of subtree
 *
 * @param node
 * @param subtree
 * @returns {boolean}
 */
function isChild(node, subtree) {
	let nt = node.type;
	let st = subtree.type;
	let sl = st.length;
	if (sl !== nt.length - 1) {
		return false;
	}
	for (let i = 0; i < sl; i++) {
		if (nt[i] !== st[i]) {
			return false;
		}
	}
	return true;
}

function EventsTree({tree}) {
	const [selectedNodes, dispatch] = useNodes();
	const [expandedNodes, setExpanded] = useState(new ImmutableSet());

	const onSelectToggle = useCallback((node) => {
		dispatch(ToggleNode, node);
	}, [dispatch]);

	const onExpandToggle = useCallback((node) => {
		setExpanded((expanded) => {
			if (expanded.has(node)) {
				return expanded.delete(node);
			} else {
				return expanded.add(node);
			}
		});
	}, [setExpanded]);

	return useMemo(() => {
		if (!tree) {
			return 'Events tree is empty!'
		}

		let showNodes = [];
		next_node: for (let node of tree) {
			let ln = node.type.length;
			if (ln === 1) {
				// Show all root nodes
				showNodes.push(node);
				continue;
			}

			for (let subtree of [...selectedNodes.values(), ...expandedNodes.values()]) {
				if (shouldShow(node, subtree)) {
					showNodes.push(node);
					continue next_node;
				}
			}
		}

		showNodes.sort(sortNodes);
		let nodes = [];
		for (let i = 0; i < showNodes.length; i++) {
			let node = showNodes[i];
			let nextNode = showNodes[i + 1];
			let expanded = Boolean(nextNode) && isChild(nextNode, node);
			nodes.push(
				<EventsTreeNode
					key={node.id} node={node}
					active={selectedNodes.has(node.id)}
					expanded={expanded}
					{...{onExpandToggle, onSelectToggle}}
				/>
			);
		}

		return (
			<>{nodes}</>
		)
	}, [tree, selectedNodes, expandedNodes]);

}

export default EventsTree;